import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { SlotCounter } from '@/components/ui/slot-counter';
import clsx from 'clsx';
import { useGamenics, useUserGoals, GamenicsEvent } from '@/hooks/useGamenics';
import { Icon } from './icons/Icon';

/**
 * GamenicsWidget — 連続記録（Streak）＋ 直近目標3つの常時表示
 *
 * - 目標は useUserGoals().nearTermGoals（未完了・priority上位3つ）。
 *   「個は完了させ、全体は未完了に保つ」（調停原則6）のUI適用。
 * - ストリーク日数は本物の実績値のみ表示（調停原則3: 事実のみ）。
 * - 未ログイン時は非表示（目標・記録が存在しないため）。
 */
export const GamenicsWidget = () => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const { playSound } = useGamenics();
    const { nearTermGoals, loading: goalsLoading } = useUserGoals();

    useEffect(() => {
        api.getUserProfile()
            .then(profileRes => {
                if (profileRes.success && profileRes.data?.user) setUser(profileRes.data.user);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading || !user) return null;

    return (
        <div className="w-full max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="col-span-1 pop-card p-5 flex flex-col items-center justify-center text-center">
                <div className="text-xs font-display font-bold text-ink-2 uppercase tracking-widest mb-3">連続記録 (Streak)</div>
                <div className="w-16 h-16 rounded-full bg-sun border-2 border-ink flex items-center justify-center mb-3">
                    <Icon name="star" size={30} className="text-ink" />
                </div>
                <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-display font-black text-ink tabular-nums"><SlotCounter value={user.streakCount || 0} /></span>
                    <span className="text-ink-2 font-bold">DAYS</span>
                </div>
                <p className="text-xs text-ink-2 mt-2">毎日アクションして連続記録を伸ばそう！</p>
            </div>

            <div className="col-span-1 md:col-span-2 pop-card p-5">
                <h3 className="font-display font-bold text-ink mb-1">あなたの直近目標</h3>
                <p className="text-sm text-ink-2 mb-4">目標を達成してコレクションや機能を開放しよう。</p>
                {goalsLoading ? (
                    <div className="space-y-3" aria-busy="true" aria-label="目標を読み込み中">
                        {[0, 1, 2].map(i => <div key={i} className="h-12 bg-cream rounded-pop-sm animate-pulse" />)}
                    </div>
                ) : nearTermGoals.length === 0 ? (
                    <div className="text-ink-2 text-sm">
                        次の目標を準備中です。作品づくりを進めると、新しい目標が届きます。
                    </div>
                ) : (
                    <div className="space-y-3">
                        {nearTermGoals.map(g => (
                            <div
                                key={g.id}
                                onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}
                                className="flex items-center justify-between bg-cream p-3 rounded-pop-sm border-2 border-ink/15 cursor-pointer hover:bg-cream-2 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={clsx("w-5 h-5 rounded border-2 flex items-center justify-center", g.isCompleted ? "bg-role-voice border-ink text-white" : "border-ink/40")}>
                                        {g.isCompleted ? <Icon name="check" size={12} /> : null}
                                    </div>
                                    <span className={clsx("text-sm", g.isCompleted ? "text-ink-3 line-through" : "text-ink")}>{g.displayText}</span>
                                </div>
                                <div className="text-xs font-bold text-primary tabular-nums">{g.currentValue} / {g.targetValue}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
